import { PRIVATE_MIRROR } from '../lib/feedContext';
/**
 * Окно практика: план / прибыль, кэф или %, добор к плечу.
 * Числа из того же стора, что стол. Счёт — `lib/calcFork`, Decimal.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { Decimal } from 'decimal.js';

import { feedKey } from '../lib/feedContext';
import {
  type ForkCur,
  type ForkKind,
  type ForkMode,
  moneyText,
  oddsToProbPct,
  pctText,
  placedFork,
  planFork,
  quoteToOdds,
  sanitizeForkNum,
  toDisplay,
} from '../lib/calcFork';
import { isShareVenue, lookLikeShareCents, oddsFromShareLock } from '../lib/shareVenue';
import { HAND_SOURCE, type ExchangeStake, type RowLeg } from '../lib/row';
import { copy } from './Grab';
import { venueName } from './meta';
import { holdLegs } from './order';
import { rowWithLock, type FeedRow } from './rows';
import {
  editAmount,
  isRubStake,
  lockAsOf,
  lockForm,
  stakeLockBody,
  useShow,
} from './show';
import { tempoOf, useSettings } from '../store/settings';
import { useScreen } from './store';

const MODE_KEY = feedKey('forq.next.calc-fork');

type Draft = {
  mode: ForkMode;
  kind: [ForkKind, ForkKind];
  cur: [ForkCur, ForkCur];
  totalCur: ForkCur;
  win: 1 | 2;
  quote: [string, string];
  stake: [string, string];
  total: string;
};

function readDraft(): Pick<Draft, 'mode' | 'kind' | 'totalCur' | 'win'> {
  try {
    const raw = localStorage.getItem(MODE_KEY);
    if (raw === null) return { mode: 'placed', kind: ['odds', 'odds'], totalCur: 'USD', win: 1 };
    const bag = JSON.parse(raw) as Partial<Draft>;
    return {
      mode: bag.mode === 'total' ? 'total' : 'placed',
      kind: [bag.kind?.[0] === 'prob' ? 'prob' : 'odds', bag.kind?.[1] === 'prob' ? 'prob' : 'odds'],
      totalCur: bag.totalCur === 'RUB' ? 'RUB' : 'USD',
      win: bag.win === 2 ? 2 : 1,
    };
  } catch {
    return { mode: 'placed', kind: ['odds', 'odds'], totalCur: 'USD', win: 1 };
  }
}

function shareQuote(leg: Pick<RowLeg, 'source' | 'unit'>): boolean {
  return leg.unit === 'share' || isShareVenue(leg.source);
}

function centsFromOdds(odds: string): string | null {
  try {
    const n = new Decimal(odds.trim().replace(',', '.'));
    if (!n.gt(1)) return null;
    const cents = new Decimal(100).div(n);
    return cents.isInteger() ? cents.toFixed(0) : cents.toDecimalPlaces(1).toString();
  } catch {
    return null;
  }
}

function liveQuote(leg: RowLeg, forkId: string, oddsLock: { forkId: string; by: Record<string, string> } | null): string {
  if (leg.source === HAND_SOURCE) return leg.odds;
  const locked = oddsLock?.forkId === forkId ? oddsLock.by[leg.source] : undefined;
  const share = shareQuote(leg);
  if (share) {
    const raw = locked ?? leg.odds;
    return (locked !== undefined || !PRIVATE_MIRROR) && lookLikeShareCents(Number(raw)) ? oddsFromShareLock(raw) : raw;
  }
  return locked ?? leg.odds;
}

function Sw({
  on,
  label,
  press,
}: {
  on: boolean;
  label: string;
  press: () => void;
}): React.ReactElement {
  return (
    <button type="button" className={on ? 'on' : undefined} aria-pressed={on} onClick={press}>
      {label}
    </button>
  );
}

export function CalcFork({
  row,
  stale = false,
}: {
  row: FeedRow | null;
  stale?: boolean;
}): React.ReactElement {
  const meta = useScreen((s) => s.meta);
  const stakeLock = useScreen((s) => s.stakeLock);
  const setStakeLock = useScreen((s) => s.setStakeLock);
  const oddsLock = useScreen((s) => s.oddsLock);
  const setOddsQuote = useScreen((s) => s.setOddsQuote);
  const setCurQuote = useScreen((s) => s.setCurQuote);
  const feeLock = useScreen((s) => s.feeLock);
  const curLock = useScreen((s) => s.curLock);
  const freeLock = useScreen((s) => s.freeLock);
  const handLock = useScreen((s) => s.handLock);
  const bank = useSettings((s) => s.bank);
  const fx = useSettings((s) => s.fx);
  const caps = useSettings((s) => s.caps);
  const freebets = useSettings((s) => s.freebets);
  const freebetsCur = useSettings((s) => s.freebetsCur);
  const attempts = useSettings((s) => s.attempts);
  const roundStep = useSettings((s) => s.roundStep);
  const roundDir = useSettings((s) => s.roundDir);
  const roundWho = useSettings((s) => s.roundWho);
  const exchangeStake = useSettings((s) => s.exchangeStake);
  const unit = useShow((s) => s.unit);
  const saved = useRef(readDraft());
  const [mode, setMode] = useState<ForkMode>(saved.current.mode);
  const [kind, setKind] = useState<[ForkKind, ForkKind]>(saved.current.kind);
  const [cur, setCur] = useState<[ForkCur, ForkCur]>(saved.current.totalCur === 'RUB' ? ['RUB', 'RUB'] : ['USD', 'USD']);
  const [totalCur, setTotalCur] = useState<ForkCur>(saved.current.totalCur);
  const [win, setWin] = useState<1 | 2>(saved.current.win);
  const [quote, setQuote] = useState<[string, string]>(['', '']);
  const [stake, setStake] = useState<[string, string]>(['', '']);
  const [total, setTotal] = useState('');
  const typed = useRef({ quote: [false, false], stake: [false, false], total: false });
  const [clip, setClip] = useState('');

  const card = row === null
    ? null
    : rowWithLock(row, {
        bank,
        fx,
        caps,
        freebets,
        freebetsCur,
        tempo: tempoOf(attempts),
        roundStep,
        roundDir,
        roundWho,
        lock: stakeLock ?? undefined,
        odds: oddsLock ?? undefined,
        fees: feeLock ?? undefined,
        curs: curLock ?? undefined,
        free: freeLock ?? undefined,
        hand: handLock ?? undefined,
      });
  const seats = useScreen((s) => (row === null ? undefined : s.seatLock[row.id]));
  const legs = useMemo(() => {
    if (card === null) return [];
    return holdLegs(card.row.legs, seats).filter((leg) => leg.source !== HAND_SOURCE).slice(0, 2);
  }, [card, seats]);

  useEffect(() => {
    try {
      localStorage.setItem(MODE_KEY, JSON.stringify({ mode, kind, totalCur, win }));
    } catch {
      /* приватный режим */
    }
  }, [mode, kind, totalCur, win]);

  useEffect(() => {
    typed.current = { quote: [false, false], stake: [false, false], total: false };
  }, [row?.id]);

  useEffect(() => {
    if (row === null || card === null || legs.length < 1) return;
    const nextQ: [string, string] = [...quote];
    const nextS: [string, string] = [...stake];
    const nextC: [ForkCur, ForkCur] = [...cur];
    legs.forEach((leg, index) => {
      if (index > 1) return;
      const live = liveQuote(leg, row.id, oddsLock);
      const as: ExchangeStake | undefined = leg.unit === 'share'
        ? lockAsOf(stakeLock?.forkId === row.id ? stakeLock : null, leg.source, exchangeStake)
        : undefined;
      const shown = editAmount(leg, fx, meta, as);
      nextC[index] = isRubStake(leg, meta) ? 'RUB' : 'USD';
      if (!typed.current.quote[index]!) {
        nextQ[index] = kind[index] === 'prob'
          ? oddsToProbPct(new Decimal(live.replace(',', '.'))).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toFixed(2)
          : live;
      }
      if (!typed.current.stake[index]!) nextS[index] = shown;
    });
    setCur(nextC);
    setQuote(nextQ);
    setStake(nextS);
    if (!typed.current.total) {
      const shown = unit === 'rub' || !(fx > 0)
        ? String(Math.round(card.row.staked > 0 ? card.row.staked : bank))
        : ((card.row.staked > 0 ? card.row.staked : bank) / fx).toFixed(2);
      setTotal(shown);
      setTotalCur(unit === 'rub' ? 'RUB' : 'USD');
    }
    // живой кадр сам подставляет, пока поле не трогали
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    row?.id,
    card?.row.staked,
    card?.row.legs.map((leg) => `${leg.source}:${leg.odds}:${leg.rub}`).join('|'),
    oddsLock,
    stakeLock,
    kind,
    fx,
    unit,
    exchangeStake,
  ]);

  const fxD = new Decimal(fx > 0 ? fx : 0);
  const o1 = quoteToOdds(quote[0], kind[0]);
  const o2 = quoteToOdds(quote[1], kind[1]);
  const plan = mode === 'total' && o1 !== null && o2 !== null ? planFork(total.replace(',', '.'), o1, o2) : null;

  const placed = ((): ReturnType<typeof placedFork> => {
    if (mode !== 'placed' || o1 === null || o2 === null) return null;
    let s1: Decimal;
    let s2: Decimal;
    try {
      s1 = new Decimal(stake[0].trim().replace(',', '.'));
      s2 = new Decimal(stake[1].trim().replace(',', '.'));
    } catch {
      return null;
    }
    if (!s1.isFinite() || !s2.isFinite() || !s1.gt(0) || !s2.gt(0)) return null;
    const show = totalCur;
    const d1 = toDisplay(s1, cur[0], show, fxD);
    const d2 = toDisplay(s2, cur[1], show, fxD);
    if (d1 === null || d2 === null) return null;
    return win === 1 ? placedFork(d1, o1, d2, o2) : placedFork(d2, o2, d1, o1);
  })();

  const lockOdds = (index: 0 | 1, form: string): void => {
    const leg = legs[index];
    if (row === null || leg === undefined) return;
    const odds = quoteToOdds(form, kind[index]);
    if (odds === null) return;
    const shown = odds.toDecimalPlaces(3, Decimal.ROUND_DOWN).toFixed(3);
    if (shareQuote(leg)) {
      const cents = centsFromOdds(shown);
      if (cents === null) return;
      setOddsQuote(row.id, leg.source, cents);
      return;
    }
    setOddsQuote(row.id, leg.source, shown);
  };

  const lockStake = (index: 0 | 1, form: string): void => {
    const leg = legs[index];
    if (row === null || leg === undefined) return;
    const as = leg.unit === 'share'
      ? lockAsOf(stakeLock?.forkId === row.id ? stakeLock : null, leg.source, exchangeStake)
      : undefined;
    setStakeLock(stakeLockBody(row.id, leg.source, form.replace(',', '.'), leg.unit, as));
  };

  const applyPlan = (): void => {
    if (plan === null || row === null || legs.length < 2) return;
    const parts = [plan.stake1, plan.stake2];
    const anchor = legs[0]!;
    const amount = totalCur === 'USD' && fx > 0 ? parts[0]!.times(fx) : parts[0]!;
    const as = anchor.unit === 'share'
      ? lockAsOf(stakeLock?.forkId === row.id ? stakeLock : null, anchor.source, exchangeStake)
      : undefined;
    setStakeLock(
      stakeLockBody(row.id, anchor.source, lockForm(amount.toNumber(), anchor, fx, meta, as), anchor.unit, as),
    );
  };

  const applyTopUp = (): void => {
    if (placed == null || placed.topUp == null || row === null) return;
    const index = (win - 1) as 0 | 1;
    const leg = legs[index];
    if (leg === undefined) return;
    const shown = toDisplay(placed.topUp, totalCur, cur[index], fxD);
    if (shown === null) return;
    let now: Decimal;
    try {
      now = new Decimal(stake[index]!.replace(',', '.') || '0');
    } catch {
      return;
    }
    const next = now.plus(shown).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toFixed(2);
    typed.current.stake[index] = true;
    setStake((prev) => {
      const copyS: [string, string] = [...prev];
      copyS[index] = next;
      return copyS;
    });
    lockStake(index, next);
  };

  const lastCopy = ((): string => {
    if (mode === 'total' && plan !== null) return `${moneyText(plan.stake1)}\t${moneyText(plan.stake2)}`;
    if (mode === 'placed' && placed !== null && o1 !== null && o2 !== null) {
      const s1 = new Decimal(stake[0].replace(',', '.'));
      const s2 = new Decimal(stake[1].replace(',', '.'));
      const n1 = win === 1 ? s1.times(o1.minus(1)) : s1.neg();
      const n2 = win === 2 ? s2.times(o2.minus(1)) : s2.neg();
      return `${moneyText(n1)}\t${moneyText(n2)}`;
    }
    return '';
  })();

  const sym = totalCur === 'USD' ? '$' : '₽';
  const names = [
    legs[0] !== undefined ? venueName(meta, legs[0].source) : 'Плечо 1',
    legs[1] !== undefined ? venueName(meta, legs[1].source) : 'Плечо 2',
  ] as const;

  const setQ = (index: 0 | 1, value: string): void => {
    const clean = sanitizeForkNum(value);
    typed.current.quote[index] = true;
    setQuote((prev) => {
      const next: [string, string] = [...prev];
      next[index] = clean;
      return next;
    });
    lockOdds(index, clean);
  };
  const setS = (index: 0 | 1, value: string): void => {
    const clean = sanitizeForkNum(value);
    typed.current.stake[index] = true;
    setStake((prev) => {
      const next: [string, string] = [...prev];
      next[index] = clean;
      return next;
    });
    lockStake(index, clean);
  };

  const tabIds = mode === 'placed' ? ['v0', 's0', 'v1', 's1'] : ['v0', 'v1', 'total'];

  return (
    <div
      className="nx-fork"
      onKeyDown={(event) => {
        const el = event.target;
        if (!(el instanceof HTMLInputElement)) return;
        if (event.key === 'ю' || event.key === 'Ю' || event.key === '/' || event.key === '.' || event.key === ',') {
          event.preventDefault();
          const next = `${el.value}.`;
          el.value = next;
          el.dispatchEvent(new Event('input', { bubbles: true }));
          return;
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
          event.preventDefault();
          const idx = tabIds.indexOf(el.id);
          const next = tabIds[(idx + 1) % tabIds.length];
          if (next !== undefined) el.ownerDocument.getElementById(next)?.focus();
        }
      }}
    >
      <div className="nx-fork__mbar">
        <Sw on={mode === 'total'} label="План" press={() => setMode('total')} />
        <Sw on={mode === 'placed'} label="Прибыль" press={() => setMode('placed')} />
      </div>

      {([0, 1] as const).map((index) => (
        <div key={index} className="nx-fork__bx">
          <div className="nx-fork__ti">■ {names[index]}</div>
          <div className="nx-fork__row nx-fork__row--sw">
            <div className="nx-fork__sw">
              <Sw on={kind[index] === 'odds'} label="Кэф" press={() => setKind((prev) => {
                const next: [ForkKind, ForkKind] = [...prev];
                next[index] = 'odds';
                typed.current.quote[index] = false;
                return next;
              })} />
              <Sw on={kind[index] === 'prob'} label="%" press={() => setKind((prev) => {
                const next: [ForkKind, ForkKind] = [...prev];
                next[index] = 'prob';
                typed.current.quote[index] = false;
                return next;
              })} />
            </div>
            {mode === 'placed' ? (
              <div className="nx-fork__sw">
                <Sw on={cur[index] === 'USD'} label="$" press={() => {
                  setCur((prev) => {
                    const next: [ForkCur, ForkCur] = [...prev];
                    next[index] = 'USD';
                    return next;
                  });
                  const leg = legs[index];
                  if (row !== null && leg !== undefined) setCurQuote(row.id, leg.source, 'USD');
                }} />
                <Sw on={cur[index] === 'RUB'} label="₽" press={() => {
                  setCur((prev) => {
                    const next: [ForkCur, ForkCur] = [...prev];
                    next[index] = 'RUB';
                    return next;
                  });
                  const leg = legs[index];
                  if (row !== null && leg !== undefined) setCurQuote(row.id, leg.source, 'RUB');
                }} />
              </div>
            ) : null}
          </div>
          <div className="nx-fork__row">
            <input
              id={`v${index}`}
              value={quote[index]}
              placeholder={kind[index] === 'odds' ? (index === 0 ? '2.10' : '2.05') : '48'}
              inputMode="decimal"
              aria-label={`${names[index]} ${kind[index] === 'odds' ? 'кэф' : 'процент'}`}
              onChange={(event) => setQ(index, event.target.value)}
            />
            {mode === 'placed' ? (
              <input
                id={`s${index}`}
                value={stake[index]}
                placeholder="500"
                inputMode="decimal"
                aria-label={`${names[index]} сумма`}
                onChange={(event) => setS(index, event.target.value)}
              />
            ) : null}
          </div>
        </div>
      ))}

      {mode === 'total' ? (
        <div className="nx-fork__bx">
          <div className="nx-fork__row">
            <input
              id="total"
              value={total}
              placeholder="1000"
              inputMode="decimal"
              aria-label="банк"
              onChange={(event) => {
                typed.current.total = true;
                setTotal(sanitizeForkNum(event.target.value));
              }}
            />
            <div className="nx-fork__sw">
              <Sw on={totalCur === 'USD'} label="$" press={() => setTotalCur('USD')} />
              <Sw on={totalCur === 'RUB'} label="₽" press={() => setTotalCur('RUB')} />
            </div>
          </div>
        </div>
      ) : (
        <div className="nx-fork__bx">
          <div className="nx-fork__ti">Какое плечо выиграло?</div>
          <div className="nx-fork__sw">
            <Sw on={win === 1} label={names[0]} press={() => setWin(1)} />
            <Sw on={win === 2} label={names[1]} press={() => setWin(2)} />
          </div>
          <div className="nx-fork__sw nx-fork__sw--cur">
            <Sw on={totalCur === 'USD'} label="$" press={() => setTotalCur('USD')} />
            <Sw on={totalCur === 'RUB'} label="₽" press={() => setTotalCur('RUB')} />
          </div>
        </div>
      )}

      <div className="nx-fork__res" aria-live="polite">
        {stale ? <div className="nx-fork__stale">в ленте этой вилки нет, числа последние</div> : null}
        {mode === 'total' && plan !== null && plan.edge.lt(0) ? (
          <div className="nx-fork__row-r"><span>Вилка</span><b className="r">нет ({pctText(plan.edge)})</b></div>
        ) : null}
        {mode === 'total' && plan !== null && !plan.edge.lt(0) ? (
          <>
            <div className="nx-fork__row-r"><span>Вилка</span><b className="g">{pctText(plan.edge)}</b></div>
            <div className="nx-fork__row-r"><span>Ставка 1</span><b>{sym}{moneyText(plan.stake1)}</b></div>
            <div className="nx-fork__row-r"><span>Ставка 2</span><b>{sym}{moneyText(plan.stake2)}</b></div>
            <div className="nx-fork__row-r"><span>Прибыль</span><b className={plan.profit.gte(0) ? 'g' : 'r'}>{sym}{moneyText(plan.profit)}</b></div>
          </>
        ) : null}
        {mode === 'placed' && placed === null ? <div className="nx-fork__wait">Укажите суммы</div> : null}
        {mode === 'placed' && placed !== null ? (
          <>
            <div className="nx-fork__row-r">
              <span>Вилка</span>
              <b className={placed.edge.gt(0) ? 'g' : 'r'}>{pctText(placed.edge)}</b>
            </div>
            <div className="nx-fork__row-r"><span>Выиграло</span><b>{names[win - 1]}</b></div>
            <div className="nx-fork__row-r">
              <span>Чистая прибыль</span>
              <b className={placed.pnl.gte(0) ? 'g' : 'r'}>{sym}{moneyText(placed.pnl)}</b>
            </div>
            {placed.topUp !== null ? (
              <div className="nx-fork__fix">
                <div className="nx-fork__ti2">⚠ Доплата к плечу {win}</div>
                <div className="nx-fork__row-r">
                  <span>Доплатить</span>
                  <b className="g">{sym}{moneyText(placed.topUp)}</b>
                </div>
                {row !== null ? (
                  <button type="button" className="nx-fork__cpy" onClick={applyTopUp}>
                    доплатить в сканер
                  </button>
                ) : null}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
      {lastCopy !== '' ? (
        <button
          type="button"
          className="nx-fork__cpy"
          onClick={() => {
            void copy(lastCopy);
            setClip('Скопировано');
            window.setTimeout(() => setClip(''), 1200);
          }}
        >
          {clip || 'Скопировать'}
        </button>
      ) : null}
      {mode === 'total' && plan !== null && row !== null && !plan.edge.lt(0) ? (
        <button type="button" className="nx-fork__cpy" onClick={applyPlan}>
          разложить в сканер
        </button>
      ) : null}
      <div className="nx-fork__bott">
        {fx > 0 ? `курс сканера: 1$ = ${moneyText(fxD)} ₽` : 'курса нет — оба плеча в одной валюте'}
      </div>
    </div>
  );
}
